/**
 * Onboarding missions — the Ring-5 tutorial chain (GDD §3: Ring 5 holds
 * "Tutorial-Missionen"; GDD §4 frames the whole UI around retention).
 *
 * Design rules this follows:
 *  - **Objectives are derived, never tracked.** Each mission asks a question
 *    about the CURRENT state ("is the smelter at level 3?"). Nothing has to be
 *    incremented at every action site, so no game action can ever forget to
 *    update a counter — and an imported save is graded correctly on load.
 *  - **Strictly sequential.** One active mission at a time, so a new player is
 *    never handed a wall of tasks.
 *  - **Rewards teach the next step.** The chain hands out the first probes and
 *    the first interceptor rather than raw resources, so the player owns the
 *    thing the following mission is about.
 *  - **Chronos stays a bonus, not a purchase replacement.** The final three
 *    missions grant 1,800 crystals total: 30% of CHRONOS_DAILY_CAP once over
 *    the entire onboarding chain, so the earnable route is meaningful but
 *    cannot dominate the shop economy.
 */

import type { GameState, ResourceAmounts } from './types.js';
import { buildingLevel } from './state.js';

export interface MissionDef {
  id: string;
  title: string;
  description: string;
  /** True once the state satisfies the objective. */
  isComplete: (state: GameState) => boolean;
  /** Short "what to do" line shown under the title. */
  hint: string;
  reward: {
    resources?: ResourceAmounts;
    xp?: number;
    ships?: Partial<Record<'probe' | 'minerShip' | 'corvette', number>>;
    defense?: Partial<Record<'interceptor', number>>;
  };
}

export const MISSIONS: MissionDef[] = [
  {
    id: 'first-mine',
    title: 'Erste Förderung',
    description:
      'Ohne Titan geht gar nichts. Die Titan-Schmelze ist das Fundament jeder Kolonie.',
    hint: 'Baue die Titan-Schmelze auf Stufe 2.',
    isComplete: (s) => buildingLevel(s, 'titanSmelter') >= 2,
    reward: { resources: { titan: 400, silicate: 200 }, xp: 60 },
  },
  {
    id: 'power-up',
    title: 'Energie sichern',
    description:
      'Minen laufen nur mit Strom. Fällt die Energiebilanz ins Minus, drosseln alle Förderanlagen.',
    hint: 'Baue das Solar-Gravitations-Kraftwerk auf Stufe 3.',
    isComplete: (s) => buildingLevel(s, 'solarPlant') >= 3,
    reward: { resources: { titan: 600, silicate: 300 }, xp: 90 },
  },
  {
    id: 'silicate',
    title: 'Hochtechnologie',
    description:
      'Kristall-Silikat ist der Rohstoff für Elektronik, Schilde und jede Forschung.',
    hint: 'Baue die Silikat-Raffinerie auf Stufe 3.',
    isComplete: (s) => buildingLevel(s, 'silicateRefinery') >= 3,
    reward: { resources: { silicate: 800 }, xp: 120 },
  },
  {
    id: 'storage',
    title: 'Lager anlegen',
    description:
      'Volle Lager stoppen die Förderung — dann geht jede weitere Tonne verloren.',
    hint: 'Baue ein Titan-Depot.',
    isComplete: (s) => buildingLevel(s, 'titanDepot') >= 1,
    reward: { resources: { titan: 1_200, silicate: 600 }, xp: 120 },
  },
  {
    id: 'lab',
    title: 'Forschung aufnehmen',
    description:
      'Das Quanten-Forschungslabor schaltet den gesamten Technologiebaum frei.',
    hint: 'Baue das Quanten-Forschungslabor.',
    isComplete: (s) => buildingLevel(s, 'researchLab') >= 1,
    reward: { resources: { isotope: 400 }, xp: 200 },
  },
  {
    id: 'first-research',
    title: 'Erste Technologie',
    description:
      'Der Impulsantrieb beschleunigt Transporter und leichte Einheiten um 10 % pro Stufe.',
    hint: 'Erforsche den Impulsantrieb auf Stufe 1.',
    isComplete: (s) => s.research.impulseDrive >= 1,
    reward: { ships: { probe: 3 }, xp: 250 },
  },
  {
    id: 'first-ships',
    title: 'Eigene Flotte',
    description:
      'Erz-Sammelschiffe bergen Asteroiden und Trümmerfelder — die beste Rohstoffquelle im Early-Game.',
    hint: 'Baue 2 Erz-Sammelschiffe.',
    isComplete: (s) => s.planets[0]!.ships.minerShip >= 2,
    reward: { resources: { titan: 3_000, silicate: 1_500, chronos: 300 }, xp: 300 },
  },
  {
    id: 'defense',
    title: 'Verteidigung aufbauen',
    description:
      'Ab Level 3 tauchen Piraten auf. Ungeschützte Kolonien werden geplündert.',
    hint: 'Baue 5 Gauß-Geschütze.',
    isComplete: (s) => s.planets[0]!.defense.gauss >= 5,
    reward: { resources: { chronos: 500 }, defense: { interceptor: 1 }, xp: 350 },
  },
  {
    id: 'silo',
    title: 'Kometenabwehr',
    description:
      'Die Silobasis produziert Abfangraketen. Ohne sie beschädigt jeder Komet deine Gebäude.',
    hint: 'Baue die Interkontinental-Silobasis.',
    isComplete: (s) => buildingLevel(s, 'missileSilo') >= 1,
    reward: { resources: { chronos: 1_000 }, defense: { interceptor: 2 }, xp: 400 },
  },
  {
    id: 'trade',
    title: 'Handel aufnehmen',
    description:
      'Die Händler-Relaisstation öffnet das Händler-Portal und senkt die Marktsteuer.',
    hint: 'Baue die Händler-Relaisstation.',
    isComplete: (s) => buildingLevel(s, 'tradeRelay') >= 1,
    reward: { resources: { credit: 2_000 }, xp: 500 },
  },
];

/** Index of the mission currently in progress, or -1 once the chain is done. */
export function activeMissionIndex(state: GameState): number {
  return state.missionIndex >= MISSIONS.length ? -1 : state.missionIndex;
}

/** The mission the player is working on, if any. */
export function activeMission(state: GameState): MissionDef | null {
  const index = activeMissionIndex(state);
  return index < 0 ? null : MISSIONS[index]!;
}
